import {observer} from 'mobx-react';
import {useEffect, useMemo} from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {Elements} from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js';
import pRetry from 'p-retry';
import {
  FeaturePageContent,
  FeaturePageHeader,
} from '@ui/molecules/feature-page-header';
import {SecondaryBreadcrumb} from '@ui/molecules/breadcrumb';
import {useStoreContext} from '../../../../../store/store-context';
import {AdminSettings} from './admin-workspace-components/admin-workspace';
import {AdminMemberPage} from '../admin-people/admin-member/admin-member-page';
import {AdminGroupTypePage} from '../admin-people/admin-group-type/admin-group-type-page';
import {AdminGroupPage} from '../admin-people/admin-group/admin-group-page';
import {GroupDetailPage} from '../admin-people/admin-group/group-detail-page';
import {AdminGoalFramework} from './workspace-performance/admin-goal-framework/admin-goal-framework.page';
import WorkSpaceEngagement from './workspace-engagements/workspace-engagements';
import {AdminBillingSetup} from './admin-billing-setup/admin-billing-setup.page';
import {BillingCycle} from './admin-billing-setup/admin-billing-components/billing-not-active/components/billng-cycle';
import {
  LegacySectionContent,
  WorkspaceSettingsContent,
} from './admin-workspace-styled';
import {deriveBillingState} from '@utils/billing-state';
import {isPaymentRecoveryRoute} from '../../../../../routes/billing-recovery-route';

const stripePromise = pRetry(
  () => loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_ID as any),
  {
    onFailedAttempt: () => {},
    retries: 5,
  },
);

export const WORKSPACE_ROUTES = {
  GENERAL: 'general',
  PROFILE: 'profile',
  VALUES: 'values',
  ENGAGEMENT: 'engagement',
  PERFORMANCE: 'performance',
  MEMBERS: 'members',
  GROUPS: 'groups',
  BILLING: 'billing',
  BILLING_CYCLE: 'billing-cycle',
};

const routeStartsWith = (pathname: string, route: string) =>
  pathname.startsWith(`/workspace-settings/${route}`);

export const AdminWorkspacePage = observer(() => {
  const {
    authStore: {auth},
    billingsStore: {updateShowNonActiveBilling},
    groupStore,
    storeDashboardRoutes: {activateWorkspaceTab},
  } = useStoreContext();
  const navigate = useNavigate();
  const {pathname} = useLocation();

  const isFreePlanActive = auth.user.workspace.billing?.plan === 'free_tier';
  const billingState = deriveBillingState(auth.user.workspace.billing);
  const paymentRequired = billingState === 'past_due';
  const isWorkspaceAccessRestricted = isFreePlanActive || paymentRequired;
  const shouldChoosePlan =
    billingState === 'trialing' ||
    billingState === 'trial_expired' ||
    billingState === 'free';
  const isGeneral =
    pathname === '/workspace-settings' ||
    pathname === '/workspace-settings/' ||
    routeStartsWith(pathname, WORKSPACE_ROUTES.GENERAL) ||
    routeStartsWith(pathname, WORKSPACE_ROUTES.PROFILE);

  useEffect(() => {
    if (paymentRequired && !isPaymentRecoveryRoute(pathname)) {
      navigate('/workspace-settings/billing', {replace: true});
      return;
    }

    if (
      pathname === '/workspace-settings' ||
      pathname === '/workspace-settings/'
    ) {
      navigate('/workspace-settings/general', {replace: true});
    }
  }, [navigate, pathname, paymentRequired]);

  const tabs = useMemo(
    () => [
      {
        id: 'general',
        label: 'General',
        active: isGeneral,
        disabled: isWorkspaceAccessRestricted,
        onClick: () => {
          activateWorkspaceTab('profile');
          navigate('/workspace-settings/general');
        },
      },
      {
        id: 'members',
        label: 'Members',
        active: routeStartsWith(pathname, WORKSPACE_ROUTES.MEMBERS),
        disabled: isWorkspaceAccessRestricted,
        onClick: () => navigate('/workspace-settings/members'),
      },
      {
        id: 'groups',
        label: 'Teams & Groups',
        active: routeStartsWith(pathname, WORKSPACE_ROUTES.GROUPS),
        disabled: isWorkspaceAccessRestricted,
        onClick: () => {
          groupStore.setIsGroup(false);
          groupStore.setGroupTypeNameInStore('');
          navigate('/workspace-settings/groups');
        },
      },
      {
        id: 'goals',
        label: 'Configure Goals',
        active: routeStartsWith(pathname, WORKSPACE_ROUTES.PERFORMANCE),
        disabled: isWorkspaceAccessRestricted,
        onClick: () => {
          activateWorkspaceTab('performance');
          navigate('/workspace-settings/performance/goal-settings');
        },
      },
      {
        id: 'billing',
        label: 'Billing',
        active:
          routeStartsWith(pathname, WORKSPACE_ROUTES.BILLING) ||
          routeStartsWith(pathname, WORKSPACE_ROUTES.BILLING_CYCLE),
        onClick: () => {
          activateWorkspaceTab('billing');
          if (shouldChoosePlan) updateShowNonActiveBilling(true);
          navigate('/workspace-settings/billing');
        },
      },
    ],
    [
      activateWorkspaceTab,
      groupStore,
      isWorkspaceAccessRestricted,
      isGeneral,
      shouldChoosePlan,
      navigate,
      pathname,
      updateShowNonActiveBilling,
    ],
  );

  const groupBreadcrumbs = useMemo(
    () => [
      {
        href: '#',
        id: 'groups',
        onClick: () => {
          groupStore.setIsGroup(false);
          groupStore.setGroupTypeNameInStore('');
        },
        title: 'Teams & Groups',
      },
      {
        href: '',
        id: 'current-group',
        isCurrent: true,
        onClick: () => {},
        title: groupStore.groupTypeNameInStore,
      },
    ],
    [groupStore],
  );

  return (
    <>
      <FeaturePageHeader tabs={tabs} ariaLabel="Workspace settings" />
      <FeaturePageContent>
        <WorkspaceSettingsContent>
          <Routes>
            <Route
              path={WORKSPACE_ROUTES.GENERAL}
              element={<AdminSettings />}
            />
            <Route
              path={WORKSPACE_ROUTES.PROFILE}
              element={<Navigate to="/workspace-settings/general" replace />}
            />
            <Route
              path={WORKSPACE_ROUTES.VALUES}
              element={<Navigate to="/workspace-settings/general" replace />}
            />
            <Route
              path={`${WORKSPACE_ROUTES.ENGAGEMENT}/*`}
              element={<WorkSpaceEngagement />}
            />
            <Route
              path={`${WORKSPACE_ROUTES.MEMBERS}/*`}
              element={
                <LegacySectionContent>
                  <AdminMemberPage />
                </LegacySectionContent>
              }
            />
            <Route
              path={`${WORKSPACE_ROUTES.GROUPS}/:groupId`}
              element={
                <LegacySectionContent>
                  <GroupDetailPage />
                </LegacySectionContent>
              }
            />
            <Route
              path={`${WORKSPACE_ROUTES.GROUPS}/*`}
              element={
                <LegacySectionContent>
                  {groupStore.isGroup ? (
                    <>
                      <SecondaryBreadcrumb navigation={groupBreadcrumbs} />
                      <AdminGroupPage />
                    </>
                  ) : (
                    <AdminGroupTypePage />
                  )}
                </LegacySectionContent>
              }
            />
            <Route
              path={`${WORKSPACE_ROUTES.PERFORMANCE}/*`}
              element={<AdminGoalFramework />}
            />
            <Route
              path={WORKSPACE_ROUTES.BILLING}
              element={
                <Elements stripe={stripePromise}>
                  <AdminBillingSetup setShowStarterPlanCTA={() => {}} />
                </Elements>
              }
            />
            <Route
              path={WORKSPACE_ROUTES.BILLING_CYCLE}
              element={
                <Elements stripe={stripePromise}>
                  <BillingCycle />
                </Elements>
              }
            />
          </Routes>
        </WorkspaceSettingsContent>
      </FeaturePageContent>
    </>
  );
});

export const adminWorkspacePage = () => {
  const Page = observer(() => <AdminWorkspacePage />);
  return {Page};
};
